import React, { useState } from 'react';
import { Popup, Table, VerticalMenu, Button, Icon } from '@scuf/common';
import { RmaModelLite } from 'models';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { RmaLite, notificationAtom } from 'resources';
import { formatDate, handleYesOrNo } from 'helpers';
import { rmaItems } from 'models/repaircenter/detailsRma';
import Alert from 'components/alert';
import { HttpResponseArquivo } from 'axios-http';
import useUser from 'helpers/hooks/useUser';
import { getReportItemsFile } from 'resources/api/repaircenter_api/getReport';

const EquipmentsAccordion: React.FC = () => {
    const { t } = useTranslation();
    const rmaDetail = useRecoilValue<RmaModelLite>(RmaLite);
    const setNotification = useSetRecoilState(notificationAtom);
    const [loading, setLoading] = useState<boolean>(false);

    async function getReport() {
        if (!loading) {
            try {
                setLoading(true);
                const response = await getReportItemsFile(rmaDetail.id);
                await downloadReportFile(response);
            } catch (e) {
                setNotification(old => ({
                    ...old,
                    message: 'ErrorDownloadingFile',
                    type: 'critical'
                }));
            } finally {
                setLoading(false);
            }
        }
    }

    async function downloadReportFile(reportData: HttpResponseArquivo) {
        if (reportData.status != 200) {
            setNotification(old => ({ ...old, message: 'NoResult', type: 'information' }));
            setLoading(false);
        } else {
            const blob = new Blob([reportData.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `report${'Equipments'}`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }
    return (
        <>
            {rmaDetail?.rmaItems !== null ? (
                <div>
                    <Popup
                        className="popup-theme-wrap"
                        element={
                            <Button
                                type="link"
                                content={t('actions.Export')}
                                icon={<Icon name="ellipsis-vertical" root="common" size="large" />}
                            />
                        }
                        on="click"
                    >
                        <VerticalMenu>
                            <VerticalMenu.Item onClick={() => getReport()}>{t('actions.ExportXLSX')}</VerticalMenu.Item>
                        </VerticalMenu>
                    </Popup>
                    <Table>
                        <Table.Header>
                            <Table.HeaderCell content={t('translation.Model')} />
                            <Table.HeaderCell content={t('repaircenter:manage_eos.translation.ProblemLocation')} />
                            <Table.HeaderCell content={t('translation.Description')} />
                            <Table.HeaderCell content={t('translation.SerialNumber')} />
                            <Table.HeaderCell content={t('status.Approved')} />
                            <Table.HeaderCell content={t('translation.PartNumber')} />
                            <Table.HeaderCell content={t('repaircenter:translation.ModalityOS')} />
                            <Table.HeaderCell content={t('repaircenter:rma.budget.ServiceWarranty')} />
                            <Table.HeaderCell content={t('repaircenter:rma.budget.FactoryWarranty')} />
                            <Table.HeaderCell content={t('translation.MaintenanceContract')} />
                            <Table.HeaderCell content={t('repaircenter:translation.CodeContract')} />
                            <Table.HeaderCell content={t('repaircenter:translation.Distribution')} />
                            <Table.HeaderCell content={t('repaircenter:rma.budget.DistributionWarranty')} />
                        </Table.Header>
                        <Table.Body>
                            {rmaDetail
                                ? [
                                      rmaDetail?.rmaItems?.map((item: rmaItems) => (
                                          <Table.Row>
                                              <Table.Cell>{item?.model}</Table.Cell>
                                              <Table.Cell>{item.rmaItemsEquipment?.problemLocation?.name ?? ''}</Table.Cell>
                                              <Table.Cell>
                                                  {item.rmaItemsAccessories?.problemDescription ??
                                                      item.rmaItemsEquipment?.problemType?.name ??
                                                      item.rmaItemsEquipment?.problemTypeDescription ??
                                                      ''}
                                              </Table.Cell>
                                              <Table.Cell>{item.rmaItemsEquipment?.serialNumber ?? ''}</Table.Cell>
                                              <Table.Cell>{handleYesOrNo(item.approved)}</Table.Cell>
                                              <Table.Cell>
                                                  {item.rmaItemsEquipment?.partNumber ?? t('repaircenter:translation.NoData')}
                                              </Table.Cell>
                                              <Table.Cell>
                                                  {item.rmaItemsEquipment?.modalityOS ?? t('repaircenter:translation.NoData')}
                                              </Table.Cell>
                                              <Table.Cell>
                                                  {formatDate(item.rmaItemsEquipment?.serviceWarranty)
                                                      ? formatDate(item.rmaItemsEquipment?.serviceWarranty)
                                                      : t('repaircenter:translation.NoData')}
                                              </Table.Cell>
                                              <Table.Cell>
                                                  {formatDate(item.rmaItemsEquipment?.factoryWarranty)
                                                      ? formatDate(item.rmaItemsEquipment?.factoryWarranty)
                                                      : t('repaircenter:translation.NoData')}
                                              </Table.Cell>
                                              <Table.Cell>
                                                  {formatDate(item.rmaItemsEquipment?.maintenanceContract)
                                                      ? formatDate(item.rmaItemsEquipment?.maintenanceContract)
                                                      : t('repaircenter:translation.NoData')}
                                              </Table.Cell>
                                              <Table.Cell>
                                                  {item.rmaItemsEquipment?.contractCode ?? t('repaircenter:translation.NoData')}
                                              </Table.Cell>
                                              <Table.Cell>
                                                  {item.rmaItemsEquipment?.distribution === true
                                                      ? t('repaircenter:translation.Yes')
                                                      : item.rmaItemsEquipment?.distribution === false
                                                        ? t('repaircenter:translation.No')
                                                        : t('repaircenter:translation.NoData')}
                                              </Table.Cell>
                                              <Table.Cell>
                                                  {formatDate(item.rmaItemsEquipment?.distributionWarranty)
                                                      ? formatDate(item.rmaItemsEquipment?.distributionWarranty)
                                                      : t('repaircenter:translation.NoData')}
                                              </Table.Cell>
                                          </Table.Row>
                                      ))
                                  ]
                                : '--'}
                        </Table.Body>
                    </Table>
                </div>
            ) : (
                <Alert text={t('empty.NoShipping')}></Alert>
            )}
        </>
    );
};

export default EquipmentsAccordion;
